import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, EVENT_LABEL, VOICES } from "../../lib/api";
import { fmtDateLong, fmtTime, lisbonParts, fromLisbon } from "../../lib/format";
import { Spinner, EmptyState, Button, Field, Chip, Sheet } from "../../components/ui";
import { Icon } from "../../components/icons";
import { can, canSeeAvailability } from "../../lib/permissions";
import { EventAvailability } from "../../components/EventAvailability";
import { useAuth } from "../../auth";

const TYPES = Object.keys(EVENT_LABEL); // ensaio, concerto, outro

// Event times are edited in Lisbon time, whatever the device time zone.
const toDateInput = (iso) => (iso ? lisbonParts(iso).date : "");
const toTimeInput = (iso) => (iso ? lisbonParts(iso).time : "19:00");

// DD/MM/AAAA text input that keeps the ISO date (AAAA-MM-DD) in state, independent of the browser locale.
function DateInputPT({ value, onChange, testid }) {
  const toTxt = (v) => (v ? v.split("-").reverse().join("/") : "");
  const [txt, setTxt] = useState(toTxt(value));
  const change = (raw) => {
    const d = raw.replace(/\D/g, "").slice(0, 8);
    const t = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
    setTxt(t);
    const [dd, mm, yyyy] = t.split("/");
    const iso = `${yyyy}-${mm}-${dd}`;
    const ok = yyyy?.length === 4 && !Number.isNaN(new Date(`${iso}T12:00`).getTime()) && new Date(`${iso}T12:00`).getDate() === Number(dd);
    onChange(ok ? iso : "");
  };
  return <input className="input" inputMode="numeric" placeholder="DD/MM/AAAA" value={txt} onChange={(e) => change(e.target.value)} aria-label="Data (DD/MM/AAAA)" data-testid={testid} />;
}

// HH:MM 24-hour text input.
function TimeInput24({ value, onChange, testid }) {
  const [txt, setTxt] = useState(value || "");
  const change = (raw) => {
    const d = raw.replace(/\D/g, "").slice(0, 4);
    const t = d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
    setTxt(t);
    onChange(/^([01]\d|2[0-3]):[0-5]\d$/.test(t) ? t : "");
  };
  return <input className="input" inputMode="numeric" placeholder="HH:MM" value={txt} onChange={(e) => change(e.target.value)} aria-label="Hora (24 h)" data-testid={testid} />;
}

function Editor({ existing, setlists, members, allSongs, onClose, onSaved }) {
  const { user: me } = useAuth();
  const [type, setType] = useState(existing?.type || "ensaio");
  const [title, setTitle] = useState(existing?.title || "");
  const [date, setDate] = useState(toDateInput(existing?.starts_at));
  const [time, setTime] = useState(toTimeInput(existing?.starts_at));
  const [location, setLocation] = useState(existing?.location || "");
  const [address, setAddress] = useState(existing?.address || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [setlistId, setSetlistId] = useState(existing?.setlist_id ? String(existing.setlist_id) : "");
  const [songIds, setSongIds] = useState((existing?.song_ids || existing?.songs?.map((s) => s.id) || []).map(String));
  const [singers, setSingers] = useState((existing?.member_ids || []).map(String));
  const [error, setError] = useState("");

  useEffect(() => {
    if (setlistId && songIds.length === 0) {
      const sl = setlists.find((s) => String(s.id) === String(setlistId));
      if (sl) {
        const ids = (sl.song_ids?.length ? sl.song_ids : (sl.songs || []).map((s) => s.id)).map(String);
        setSongIds(ids);
        if (sl.member_ids?.length) setSingers(sl.member_ids.map(String));
        if (!title) setTitle(sl.name || sl.title || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setlistId, setlists]);

  const pickTemplate = (id) => {
    setSetlistId(id);
    const sl = setlists.find((s) => String(s.id) === String(id));
    if (sl) {
      const ids = (sl.song_ids?.length ? sl.song_ids : (sl.songs || []).map((s) => s.id)).map(String);
      setSongIds(ids);
      if (sl.member_ids?.length) setSingers(sl.member_ids.map(String));
      if (!title) setTitle(sl.name || sl.title || "");
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const when = fromLisbon(date, time || "00:00");
      const body = {
        type, title: title.trim(), starts_at: when.toISOString(),
        location: location.trim(), address: address.trim(), notes: notes.trim(),
        song_ids: songIds, member_ids: singers, setlist_id: setlistId || null,
      };
      return api(existing?.id ? `/admin/events/${existing.id}` : "/admin/events", {
        method: existing?.id ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  const toggleSinger = (id) => setSingers((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const songById = (id) => allSongs.find((s) => String(s.id) === String(id));
  const addSong = (id) => setSongIds((p) => (p.includes(String(id)) ? p : [...p, String(id)]));
  const removeSong = (id) => setSongIds((p) => p.filter((x) => x !== String(id)));
  const moveSong = (i, dir) => setSongIds((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const next = [...p];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const [songSearch, setSongSearch] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const moveSongTo = (from, to) => setSongIds((p) => {
    if (from == null || to == null || from === to) return p;
    const next = [...p];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    return next;
  });
  const available = allSongs.filter((s) => !songIds.includes(String(s.id)) && `${s.title} ${s.artist || ""}`.toLowerCase().includes(songSearch.toLowerCase()));
  const addVoice = (voice) => {
    const ids = members.filter((m) => m.voice === voice).map((m) => String(m.id));
    setSingers((p) => Array.from(new Set([...p, ...ids])));
  };
  const voiceCount = (voice) => members.filter((m) => m.voice === voice).length;

  return (
    <Sheet title={existing?.id ? "Editar evento" : "Agendar evento"} onClose={onClose} testid="event-form" wide>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <div className="t-section" style={{ marginBottom: 8 }}>Tipo</div>
          <div style={{ display: "flex", gap: 8 }}>
            {TYPES.map((t) => <Chip key={t} label={EVENT_LABEL[t].toUpperCase()} active={type === t} onClick={() => setType(t)} data-testid={`event-type-${t}`} />)}
          </div>
        </div>
        <Field label="TÍTULO" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Ensaio de naipe" data-testid="event-title" />
        <div style={{ display: "flex", gap: 12 }}>
          <label className="field" style={{ flex: 1 }}><span>DATA</span><DateInputPT value={date} onChange={setDate} testid="event-date" /></label>
          <label className="field" style={{ width: 140 }}><span>HORA</span><TimeInput24 value={time} onChange={setTime} testid="event-time" /></label>
        </div>
        <Field label="LOCAL" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex.: Estúdio Alvalade" />
        <Field label="MORADA" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua, número, cidade" />
        <Field as="textarea" label="NOTAS" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Indicações para os membros" />

        <div>
          <div className="t-section" style={{ marginBottom: 8 }}>Alinhamento</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip label="SEM ALINHAMENTO" active={!setlistId} onClick={() => { setSetlistId(""); setSongIds([]); }} data-testid="event-setlist-none" />
            {setlists.map((s) => (
              <Chip key={s.id} label={(s.name || s.title || "Alinhamento").toUpperCase()} active={String(setlistId) === String(s.id)} onClick={() => pickTemplate(String(s.id))} data-testid={`event-setlist-${s.id}`} />
            ))}
          </div>
          <div className="t-hint" style={{ marginTop: 6 }}>Preencha rapidamente a partir de um alinhamento guardado, ou edite as músicas abaixo.</div>
        </div>

        <div data-testid="event-songs-editor">
          <div className="t-section" style={{ marginBottom: 8 }}>Músicas deste evento ({songIds.length})</div>
          {songIds.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
              {songIds.map((sid, i) => {
                const s = songById(sid);
                return (
                  <div
                    key={sid}
                    className="card"
                    draggable
                    onDragStart={() => setDragIndex(i)}
                    onDragOver={(ev) => ev.preventDefault()}
                    onDrop={() => { moveSongTo(dragIndex, i); setDragIndex(null); }}
                    onDragEnd={() => setDragIndex(null)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, opacity: dragIndex === i ? 0.4 : 1 }}
                    data-testid={`event-song-${sid}`}
                  >
                    <span style={{ cursor: "grab", color: "var(--muted)", display: "flex", touchAction: "none" }} title="Arraste para reordenar"><Icon name="list" size={15} /></span>
                    <span style={{ width: 20, textAlign: "center", color: "var(--muted)", fontFamily: "var(--f-medium)" }}>{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{s?.title || "Música"}</div>
                      {s?.artist ? <div className="t-hint">{s.artist}</div> : null}
                    </div>
                    <button className="icon-btn" style={{ width: 32, height: 32 }} disabled={i === 0} onClick={() => moveSong(i, -1)} data-testid={`event-song-up-${sid}`}><Icon name="chevron-up" size={15} /></button>
                    <button className="icon-btn" style={{ width: 32, height: 32 }} disabled={i === songIds.length - 1} onClick={() => moveSong(i, 1)} data-testid={`event-song-down-${sid}`}><Icon name="chevron-down" size={15} /></button>
                    <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={() => removeSong(sid)} data-testid={`event-song-remove-${sid}`}><Icon name="close" size={15} color="var(--muted)" /></button>
                  </div>
                );
              })}
            </div>
          ) : <div className="t-hint" style={{ marginBottom: 10 }}>Sem músicas. Adicione abaixo.</div>}
          <Field label="ADICIONAR MÚSICA" value={songSearch} onChange={(e) => setSongSearch(e.target.value)} placeholder="Pesquisar no repertório…" data-testid="event-song-search" />
          {songSearch ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto", marginTop: 6 }}>
              {available.length ? available.slice(0, 30).map((s) => (
                <button key={s.id} className="card" style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, textAlign: "left", cursor: "pointer" }} onClick={() => { addSong(s.id); setSongSearch(""); }} data-testid={`event-song-add-${s.id}`}>
                  <Icon name="add" size={16} color="var(--brand-primary)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{s.title}</div>
                    {s.artist ? <div className="t-hint">{s.artist}</div> : null}
                  </div>
                </button>
              )) : <div className="t-hint">Nenhuma música encontrada.</div>}
            </div>
          ) : null}
        </div>

        {members?.length ? (
          <div>
            <div className="t-section" style={{ marginBottom: 8 }}>Cantores convocados ({singers.length})</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }} data-testid="convoke-by-voice">
              {VOICES.map((v) => (
                <button key={v} className="chip" onClick={() => addVoice(v)} data-testid={`convoke-voice-${v}`} disabled={!voiceCount(v)} style={{ opacity: voiceCount(v) ? 1 : 0.4 }}>
                  <Icon name="add" size={13} /> {v.toUpperCase()}S ({voiceCount(v)})
                </button>
              ))}
              <button className="chip" onClick={() => setSingers(members.map((m) => String(m.id)))} data-testid="convoke-all"><Icon name="people" size={13} /> TODOS</button>
              <button className="chip" onClick={() => setSingers([])} data-testid="convoke-clear" disabled={!singers.length} style={{ opacity: singers.length ? 1 : 0.4 }}><Icon name="close" size={13} /> LIMPAR</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 180, overflowY: "auto" }}>
              {members.map((m) => (
                <button key={m.id} className={`chip ${singers.includes(String(m.id)) ? "active" : ""}`} onClick={() => toggleSinger(String(m.id))} data-testid={`event-singer-${m.id}`}>
                  {m.name}{m.voice ? ` · ${m.voice[0]}` : ""}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {existing?.id && canSeeAvailability(me) ? (
          <div>
            <div className="t-section" style={{ marginBottom: 8 }}>Disponibilidade e respostas</div>
            <EventAvailability eventId={existing.id} />
          </div>
        ) : null}

        {error ? <div style={{ background: "var(--error)", color: "var(--on-error)", padding: 10, borderRadius: "var(--r-md)", fontSize: 13 }}>{error}</div> : null}
        <Button loading={save.isPending} onClick={() => { setError(""); if (!title.trim()) return setError("Indique um título."); if (!date) return setError("Indique uma data válida (DD/MM/AAAA)."); if (!time) return setError("Indique uma hora válida (HH:MM, 24 h)."); save.mutate(); }} data-testid="event-save">
          {existing?.id ? "GUARDAR" : "AGENDAR"}
        </Button>
      </div>
    </Sheet>
  );
}

export default function AdminEvents() {
  const { user: me } = useAuth();
  const canDelete = can(me, "events.delete");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [editing, setEditing] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
  const end = new Date(now.getFullYear(), now.getMonth() + 6, 1).toISOString();

  const q = useQuery({ queryKey: ["admin-events", start, end], queryFn: () => api(`/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`) });
  const setlistsQ = useQuery({ queryKey: ["admin-setlists"], queryFn: () => api("/setlists") });
  const membersQ = useQuery({ queryKey: ["members-directory"], queryFn: () => api("/members/directory"), select: (l) => l.filter((m) => m.is_singer && m.active !== false) });
  const songsQ = useQuery({ queryKey: ["songs", "", ""], queryFn: () => api("/songs?q=&status=") });
  const events = [...(q.data || [])].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  // Always edit from the full event detail so saving never drops singers/songs/notes.
  const openEdit = async (e) => {
    setLoadingId(e.id);
    try {
      setEditing(await api(`/events/${e.id}`));
    } catch (err) {
      window.alert(err.message);
    } finally {
      setLoadingId(null);
    }
  };

  // Deep link: /admin/events?setlist=<id> (prefill new) or ?edit=<eventId> (edit existing).
  useEffect(() => {
    const sid = sp.get("setlist");
    const eid = sp.get("edit");
    if (sid && setlistsQ.data) {
      setEditing({ setlist_id: sid });
      sp.delete("setlist");
      setSp(sp, { replace: true });
    } else if (eid) {
      sp.delete("edit");
      setSp(sp, { replace: true });
      openEdit({ id: eid });
    }
  }, [sp, setlistsQ.data, setSp]);

  const del = useMutation({
    mutationFn: (id) => api(`/admin/events/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-events"] }),
  });

  const onSaved = () => { qc.invalidateQueries({ queryKey: ["admin-events"] }); qc.invalidateQueries({ queryKey: ["events"] }); qc.invalidateQueries({ queryKey: ["home"] }); setEditing(null); };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Agenda</h1>
        <Button className="auto sm" icon="add" onClick={() => setEditing({})} data-testid="event-new-button">AGENDAR</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 820 }}>
          {!events.length ? <EmptyState icon="calendar-outline" text="Sem eventos agendados. Crie o primeiro." /> : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {events.map((e) => (
              <div key={e.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 14 }} data-testid={`admin-event-${e.id}`}>
                <div style={{ width: 44, textAlign: "center" }}>
                  <div style={{ fontFamily: "var(--f-medium)", fontSize: 18 }}>{new Date(e.starts_at).getDate()}</div>
                  <div className="t-hint" style={{ textTransform: "uppercase" }}>{new Date(e.starts_at).toLocaleDateString("pt-PT", { month: "short" })}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{e.title}</div>
                  <div className="t-hint">{EVENT_LABEL[e.type] || "Evento"} · {fmtTime(e.starts_at)}{e.location ? ` · ${e.location}` : ""}{(e.songs?.length || e.song_ids?.length) ? ` · ${e.songs?.length ?? e.song_ids.length} músicas` : ""}</div>
                </div>
                <button className="icon-btn" onClick={() => openEdit(e)} disabled={loadingId === e.id} data-testid={`event-edit-${e.id}`}>{loadingId === e.id ? <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : <Icon name="pencil" size={16} />}</button>
                {canDelete ? <button className="icon-btn" onClick={() => window.confirm(`Apagar "${e.title}"?`) && del.mutate(e.id)} data-testid={`event-delete-${e.id}`}><Icon name="trash" size={16} color="var(--muted)" /></button> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {editing ? (
        <Editor
          existing={Object.keys(editing).length && editing.id ? editing : (editing.setlist_id ? editing : null)}
          setlists={setlistsQ.data || []}
          members={membersQ.data || []}
          allSongs={songsQ.data || []}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      ) : null}
    </>
  );
}
